<template>
  <div ref="elRoot" :class="[componentClass.root, classes]" :style="styles">
    <div v-if="mountInnerContent" :class="componentClass.element('inner')">
      <div :class="componentClass.element('header')">
        <div
          v-if="isConstrainedStartMarkerVisible"
          :class="[
            componentClass.element('constrainedMarker'),
            componentClass.className({
              element: 'constrainedMarker',
              modifier: 'start',
            }),
          ]"
          :title="`Start: ${startDateFormatted}`"
        >
          <PmIconPure :name="ICONS.ARROW_LEFT" />
        </div>

        <PmDraggablePure
          :type="DRAG_AND_DROP_TYPE.UPDATE_PROJECT_GROUP"
          :data="{ projectId: projectId, groupNumber: groupNumber }"
          :use-handle="true"
          :disabled="!updateGroupVisible"
          :class="componentClass.element('headerDragAndDrop')"
          :classes-element="componentClass.element('headerInner')"
          tag="header"
        >
          <div :class="componentClass.element('headerContent')">
            <div :class="componentClass.element('headerContentInner')">
              <div :class="componentClass.element('meta')">
                <template v-if="canEditResourceRequest">
                  <PmContextNavigationPure
                    :items="contextNavigationItems"
                    @select-considered="selectConsidered"
                    @request-considered="emit('requestConsidered')"
                  >
                    <template #trigger="slotProps">
                      <PmResourceActionPure
                        :icon="ICONS_SMALL.CONTEXT_NAVIGATION"
                        size="default"
                        :is-active="slotProps.isOpen"
                        @click="slotProps.togglePopover"
                      />
                    </template>
                  </PmContextNavigationPure>
                </template>

                <PmPillPure
                  v-if="alertsVisible && numberOfAlerts && numberOfAlerts > 0"
                  variant="danger"
                  :label="numberOfAlerts"
                />

                <PmPillPure
                  v-if="number"
                  :class="componentClass.element('number')"
                  :color="statusColorNormalized"
                  :label="number"
                />

                <div :class="componentClass.element('actions')">
                  <PmInfoIconPure
                    v-if="collectsAllJobsOfProject"
                    type="collectsAllJobsOfProject"
                    size="default"
                  />

                  <PmNoteIconPure
                    v-if="note"
                    :note="note"
                    @open="emit('openNote')"
                  />

                  <div
                    v-if="updateGroupVisible"
                    data-dragAndDrop-handle
                    :class="componentClass.element('action')"
                    :title="titleDragHandle"
                  >
                    <PmIconPure
                      :name="ICONS_SMALL.GRIP"
                      :class="componentClass.element('icon')"
                    />
                  </div>

                  <button
                    :class="componentClass.element('action')"
                    title="In Seitenleiste anzeigen"
                    @click="emit('jump')"
                  >
                    <PmIconPure
                      :name="ICONS_SMALL.JUMP"
                      :class="componentClass.element('icon')"
                    />
                  </button>

                  <PmColorPickerPure
                    v-if="alternativeColorVisible"
                    :colors="colors"
                    :selected-color-id="
                      alternativeColorNormalized
                        ? alternativeColorNormalized.id
                        : undefined
                    "
                    :is-random="
                      alternativeColorNormalized
                        ? alternativeColorNormalized.type === 'random'
                        : undefined
                    "
                    @select-color="(color) => emit('updateColor', color.id)"
                    @use-random-color="emit('useRandomColor')"
                  />
                </div>
              </div>

              <div :class="componentClass.element('titleContainer')">
                <div
                  :class="componentClass.element('title')"
                  :title="title"
                  @click="emit('showDetails')"
                >
                  {{ title }}
                </div>

                <div
                  :class="componentClass.element('subtitle')"
                  :title="subtitle"
                >
                  {{ subtitle }}
                </div>
              </div>
            </div>
          </div>
        </PmDraggablePure>

        <div
          v-if="isConstrainedEnd"
          :class="[
            componentClass.element('constrainedMarker'),
            componentClass.className({
              element: 'constrainedMarker',
              modifier: 'end',
            }),
          ]"
          :title="constrainedEndMarkerProps.title"
        >
          <PmIconPure :name="constrainedEndMarkerProps.icon" />
        </div>
      </div>

      <PmPackingContainerPure
        v-slot="{ packingContainer }"
        type="AppointmentsContainer"
        :class="componentClass.element('appointments')"
      >
        <slot
          :packing-container="packingContainer"
          :constrained-end-date="constrainedEndDate"
          :constrained-start-date="constrainedStartDate"
          :is-in-viewport="isInViewport"
        />
      </PmPackingContainerPure>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  ref,
  useTemplateRef,
  toRef,
  provide,
  type StyleValue,
  type CSSProperties,
} from 'vue'
import { debounce } from 'lodash-es'
import ColorManipulation from 'color'
import seedrandom from 'seedrandom'
import { EVENT } from '@/constants/events'
import {
  CALENDAR_ITEM_TYPE,
  FORMAT_DATETIME_SHORT,
  DRAG_AND_DROP_TYPE,
  type PackingContainerId,
  type CalendarItemType,
} from '@/constants/persoplan'
import { ICONS, ICONS_SMALL } from '@/constants/icons'
import { FEATURE_FLAG } from '@/constants/featureFlags'
import { color } from '@/assets/scss/scssVariables'
import { formatWithLocale } from '@/utilities/date'
import { jiggle } from '@/utilities/jiggle'
import EventHub from '@/eventHub'
import { default as useDateConstrainComposable } from '@/composition/useDateConstrain'
import { useJobStatus, type JobStatus } from '@/composition/useJobStatus'
import {
  useProjectStatus,
  type ProjectStatus,
} from '@/composition/useProjectStatus'
import {
  usePurchaseOrderStatus,
  type PurchaseOrderStatus,
} from '@/composition/usePurchaseOrderStatus'
import { selectionProjectOrJobHelperKey } from '@/utilities/inject'
import { useJumpTarget } from '@/composition/useJumpTarget'
import PmPillPure from '@/components/basics/PmPillPure.vue'
import PmPackingContainerPure from '@/components/persoplan/PmPackingContainer/PmPackingContainerPure.vue'
import PmIconPure from '@/components/basics/PmIcon/PmIconPure.vue'
import PmDraggablePure from '@/components/utilities/PmDraggablePure.vue'
import PmColorPickerPure from '@/components/basics/PmColorPicker/PmColorPickerPure.vue'
import PmNoteIconPure from '@/components/PmNote/PmNoteIconPure.vue'
import PmContextNavigationPure, {
  type Props as PropsContextNavigationPure,
} from '@/components/basics/PmContextNavigation/PmContextNavigationPure.vue'
import PmResourceActionPure from '@/components/persoplan/PmResourceActionPure.vue'
import PmInfoIconPure from '@/components/basics/PmInfoIconPure/PmInfoIconPure.vue'
import { useComponentClass } from '@thomasaull-shared/composables'
import { useElementVisibility, refDebounced } from '@vueuse/core'
import type { PackingItem } from '@/functional/packing'
import { never } from 'valibot'

const COMPONENT_NAME = 'PmAppointmentsContainerPure'

/**
 * Which strategy to use for performance optimization
 *
 * All strategies have in common, that they will wait for the first packing, so the insintric size is already defined. This is needed to avoid jumping content
 *
 * - `unmount` Completely removes the inner content from the DOM.
 *   - Might yield the best performance regarding total number of DOM Nodes but needs a re-pack every time it mounts again, probably negating the performance advantage
 *   - Also this would probably need optimizations to not destroy e.g. allocation edit popover when scrolled outside of the viewport
 * - `cssManual` Uses an `intersectionObserver` to manually set the `content-visibility` to `hidden`. Has the advantage that the DOM Nodes can be displayed eagerly
 * - `cssAuto` Just sets `content-visibility: auto` and leave the browser to do all decisions to when to display
 */
type PerformanceStrategy = 'unmount' | 'cssManual' | 'cssAuto' | undefined
const PERFORMANCE_STRATEGY: PerformanceStrategy =
  undefined as PerformanceStrategy

const alternativeColors = ['default', 'alternative1', 'alternative2'] as const
type AlternativeColor = (typeof alternativeColors)[number]

export interface Props {
  id: number
  title?: string
  subtitle?: string
  alertsVisible?: boolean
  numberOfAlerts?: number
  number?: string
  isHighlighted?: boolean
  jobStatus?: JobStatus
  projectStatus?: ProjectStatus
  purchaseOrderStatus?: PurchaseOrderStatus
  startDate?: Date
  endDate?: Date
  collectsAllJobsOfProject?: boolean
  type: CalendarItemType
  groupNumber?: number
  projectId?: number
  updateGroupVisible?: boolean
  disableDateConstrainStyles?: boolean
  alternativeColor?: AlternativeColor
  alternativeColorVisible?: boolean
  note?: string
  contentCanBeHidden?: boolean
  packedElement?: PackingItem
  dayWidth?: number
  // Props from useDateConstrain
  useDateConstrain?: boolean
  containerStartDate?: Date
  containerEndDate?: Date
  itemsForContentBounds?: { startDate: Date; endDate: Date }[]
  canEditResourceRequest?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  updateGroupVisible: true,
  useDateConstrain: true,
  itemsForContentBounds: () => [],
})

const emit = defineEmits<{
  (event: 'highlight'): void
  (event: 'openNote'): void
  (event: 'jump'): void
  (event: 'updateColor', id: string): void
  (event: 'useRandomColor'): void
  (event: 'showDetails'): void
  (event: 'requestConsidered'): void
}>()

const colorLookupFallback = {
  alternative1: color['gray-500'],
  default: color['gray-400'],
  alternative2: color['gray-300'],
} as const

interface ColorPalette {
  alternative1: string
  default: string
  alternative2: string
}

const purchaseOrderColorLookup = {
  requisition: {
    alternative1: color['status-purchaseOrder-requisition--alternative1'],
    default: color['status-purchaseOrder-requisition'],
    alternative2: color['status-purchaseOrder-requisition--alternative2'],
  },
  requested: {
    alternative1: color['status-purchaseOrder-requested--alternative1'],
    default: color['status-purchaseOrder-requested'],
    alternative2: color['status-purchaseOrder-requested--alternative2'],
  },
  ordered: {
    alternative1: color['status-purchaseOrder-ordered--alternative1'],
    default: color['status-purchaseOrder-ordered'],
    alternative2: color['status-purchaseOrder-ordered--alternative2'],
  },
  canceled: {
    alternative1: color['status-purchaseOrder-canceled--alternative1'],
    default: color['status-purchaseOrder-canceled'],
    alternative2: color['status-purchaseOrder-canceled--alternative2'],
  },
} as const satisfies Record<PurchaseOrderStatus, ColorPalette>

const jobColorLookup = {
  confirmed: {
    alternative1: color['status-job-confirmed--alternative1'],
    default: color['status-job-confirmed'],
    alternative2: color['status-job-confirmed--alternative2'],
  },
  requested: {
    alternative1: color['status-job-requested--alternative1'],
    default: color['status-job-requested'],
    alternative2: color['status-job-requested--alternative2'],
  },
  cancelled: {
    alternative1: color['status-job-cancelled--alternative1'],
    default: color['status-job-cancelled'],
    alternative2: color['status-job-cancelled--alternative2'],
  },
  considered: {
    alternative1: color['status-job-considered--alternative1'],
    default: color['status-job-considered'],
    alternative2: color['status-job-considered--alternative2'],
  },
  offered: {
    alternative1: color['status-job-offered--alternative1'],
    default: color['status-job-offered'],
    alternative2: color['status-job-offered--alternative2'],
  },
  service: {
    alternative1: color['status-job-service--alternative1'],
    default: color['status-job-service'],
    alternative2: color['status-job-service--alternative2'],
  },
  subHire: {
    alternative1: color['status-job-subHire--alternative1'],
    default: color['status-job-subHire'],
    alternative2: color['status-job-subHire--alternative2'],
  },
  itemTransfer: {
    alternative1: color['status-job-itemTransfer--alternative1'],
    default: color['status-job-itemTransfer'],
    alternative2: color['status-job-itemTransfer--alternative2'],
  },
} as const satisfies Record<JobStatus, ColorPalette>

const projectColorLookup = {
  confirmed: {
    alternative1: color['status-project-confirmed--alternative1'],
    default: color['status-project-confirmed'],
    alternative2: color['status-project-confirmed--alternative2'],
  },
  cancelled: {
    alternative1: color['status-project-cancelled--alternative1'],
    default: color['status-project-cancelled'],
    alternative2: color['status-project-cancelled--alternative2'],
  },
  offered: {
    alternative1: color['status-project-offered--alternative1'],
    default: color['status-project-offered'],
    alternative2: color['status-project-offered--alternative2'],
  },
  service: {
    alternative1: color['status-project-service--alternative1'],
    default: color['status-project-service'],
    alternative2: color['status-project-service--alternative2'],
  },
} as const satisfies Record<ProjectStatus, ColorPalette>

const elRoot = useTemplateRef<HTMLElement>('elRoot')
const isJiggeling = ref(false)
const isHighlightedBecauseJiggeling = ref(false)
const componentClass = useComponentClass()

/**
 * Detect if the element is in the viewport
 */
const isInViewport = useElementVisibility(elRoot, {
  // rootMargin: '-100px', // for debugging, mounts late
  rootMargin: '200px', // for production, mounts eager
})

// const isInViewportNormalized = refDebounced(isInViewport, 500)

const renderInnerContent = computed(() => {
  if (props.contentCanBeHidden !== true) return true
  return isInViewport.value
})

const mountInnerContent = computed(() => {
  if (PERFORMANCE_STRATEGY !== 'unmount') return true
  return renderInnerContent.value // Actually mount/unmount the inner part
})

const {
  containerNumberOfDays,
  isConstrainedStart,
  isConstrainedEnd,
  constrainedStartDate,
  constrainedEndDate,
  // constrainedEnd,
  // endFinal,
  constrainedStartInDirection,
  constrainedEndInDirection,
  styles: stylesDateConstrain,
  dimensions: dimensionsDateConstrain,
} = useDateConstrainComposable(props)

const jobStatusBla = useJobStatus({
  status: toRef(props, 'jobStatus'),
})

const projectStatusBla = useProjectStatus({
  status: toRef(props, 'projectStatus'),
})

const purchaseOrderStatus = usePurchaseOrderStatus({
  status: toRef(props, 'purchaseOrderStatus'),
})

const statusColorNormalized = computed(() => {
  if (props.type === 'purchaseOrder') return purchaseOrderStatus.color.value

  if (props.type === 'project') {
    return projectStatusBla.color.value
  }

  if (props.type === 'job') {
    return jobStatusBla.color.value
  }

  return undefined
})

const classes = computed(() => {
  let statusClasses = {}

  if (props.type === 'project') {
    statusClasses = projectStatusBla.classes.value ?? {}
  }

  if (props.type === 'job') {
    statusClasses = jobStatusBla.classes.value ?? {}
  }

  if (props.type === 'purchaseOrder') {
    statusClasses = purchaseOrderStatus.classes.value ?? {}
  }

  return {
    ...statusClasses,
    'is-contentCanBeHidden': props.contentCanBeHidden,
    'is-highlighted': isHighlightedNormalized.value,
    'is-jiggeling': isJiggeling.value,
  }
})

const isHighlightedNormalized = computed(() => {
  if (isHighlightedBecauseJiggeling.value) return true
  if (props.isHighlighted) return true

  return false
})

const stylesPacking = computed(() => {
  if (props.disableDateConstrainStyles) return
  if (!stylesDateConstrain.value) return
  if (!props.dayWidth) throw new Error('dayWidth is undefined')

  const containerWidth = (containerNumberOfDays.value ?? 0) * props.dayWidth

  const width = (dimensionsDateConstrain.value?.width ?? 0) * containerWidth
  const left = (dimensionsDateConstrain.value?.left ?? 0) * containerWidth

  /**
   * This technique breaks `position: sticky`:
   */
  // const transform = `translate(${left}px, ${props.packedElement?.y ?? 0}px)`
  // const result: CSSProperties = {
  //   position: 'absolute',
  //   top: 0,
  //   left: 0,
  //   width: `${width}px`,
  //   transform,
  // }

  const result: CSSProperties = {
    position: 'absolute',
    top: `${props.packedElement?.y ?? 0}px`,
    left: `${left}px`,
    width: `${width}px`,
  }

  /**
   * Only use contentVisibility when the dimensions are already determined
   */
  if (props.packedElement?.height) {
    result.containIntrinsicWidth = `${width}px`
    result.containIntrinsicHeight = `${props.packedElement?.height}px`

    if (PERFORMANCE_STRATEGY === 'cssManual') {
      if (renderInnerContent.value) {
        result.contentVisibility = 'visible'
      } else {
        result.contentVisibility = 'hidden'
      }
    }

    if (PERFORMANCE_STRATEGY === 'cssAuto') {
      result.contentVisibility = 'auto'
    }
  }

  return result
})

const styles = computed<StyleValue>(() => {
  const result = {
    [`--${COMPONENT_NAME}-colorBorder--alternativeColor`]: colorBorder.value,
    [`--${COMPONENT_NAME}-colorBackground--alternativeColor`]:
      colorBackground.value?.toString() ?? undefined,
  } satisfies CSSProperties

  return {
    ...result,
    ...stylesPacking.value,
  }
})

const titleDragHandle = computed(() => {
  if (props.type === CALENDAR_ITEM_TYPE.JOB) {
    return 'Gruppe für Projekt festlegen'
  }

  return 'Gruppe festlegen'
})

const startDateFormatted = computed(() => {
  if (!props.startDate) return
  return formatWithLocale(props.startDate, FORMAT_DATETIME_SHORT)
})

const endDateFormatted = computed(() => {
  if (!props.endDate) return
  return formatWithLocale(props.endDate, FORMAT_DATETIME_SHORT)
})

const colorLookupForStatus = computed(() => {
  if (props.type === 'purchaseOrder') {
    if (!props.purchaseOrderStatus) return colorLookupFallback

    return purchaseOrderColorLookup[props.purchaseOrderStatus]
  }

  if (props.type === 'project') {
    if (!props.projectStatus) return colorLookupFallback

    return projectColorLookup[props.projectStatus]
  }

  if (props.type === 'job') {
    if (!props.jobStatus) return colorLookupFallback

    return jobColorLookup[props.jobStatus]
  }

  return colorLookupFallback
})

/**
 * Get random alternative color by seed, so it stays the same
 */
const randomAlternativeColor = computed(() => {
  const numberOfAlternativeColors = alternativeColors.length
  const seededRandomNumber = Math.abs(seedrandom(props.id.toString()).int32())
  const index = seededRandomNumber % numberOfAlternativeColors

  return alternativeColors[index]
})

const alternativeColorNormalized = computed(() => {
  if (props.alternativeColor)
    return {
      type: 'manual',
      id: props.alternativeColor,
    }

  return {
    type: 'random',
    id: randomAlternativeColor.value,
  }
})

const colorBase = computed(() => {
  if (!colorLookupForStatus.value) return

  const color = {
    type: alternativeColorNormalized.value.type,
    id: alternativeColorNormalized.value.id,
    value: colorLookupForStatus.value[alternativeColorNormalized.value.id],
  }

  /**
   * Use the default color value which, in case a selected alternative
   * color ist nod defined for the current state
   */
  if (!color.value) {
    color.value = colorLookupForStatus.value.default
  }

  return color
})

const colorBorder = computed(() => {
  if (!colorBase.value?.value) return

  return colorBase.value.value
})

const colorBackground = computed(() => {
  if (!colorBase.value?.value) return

  const color = ColorManipulation(colorBase.value.value)
  return color.alpha(0.2)
})

const colors = computed(() => {
  if (!colorLookupForStatus.value) return

  const colors = Object.entries(colorLookupForStatus.value).map(
    ([id, color]) => {
      return {
        id,
        value: color,
      }
    }
  )

  return colors
})

const isConstrainedStartMarkerVisible = computed(() => {
  if (isConstrainedEndMarkerForBoth.value) return false
  return isConstrainedStart.value
})

const isConstrainedEndMarkerForBoth = computed(() => {
  return (
    isConstrainedStart.value &&
    constrainedStartInDirection.value === 'right' &&
    isConstrainedEnd.value &&
    constrainedEndInDirection.value === 'right'
  )
})

const constrainedEndMarkerProps = computed(() => {
  if (isConstrainedEndMarkerForBoth.value) {
    return {
      title: `${startDateFormatted.value} — ${endDateFormatted.value}`,
      icon: ICONS.DOUBLE_ARROW_RIGHT,
    }
  }

  return {
    title: `Ende: ${endDateFormatted.value}`,
    icon: ICONS.ARROW_RIGHT,
  }
})

const contextNavigationItems = computed(() => {
  let entity: undefined | 'Jobs' | 'Projekts' = undefined

  if (props.type === CALENDAR_ITEM_TYPE.JOB) {
    entity = 'Jobs'
  }

  if (props.type === CALENDAR_ITEM_TYPE.PROJECT) {
    entity = 'Projekts'
  }

  const items: PropsContextNavigationPure['items'] = [
    {
      id: 'selectConsidered',
      icon: ICONS.DUPLICATE,
      label: 'Angedachte auswählen',
      note: `Alle angedachten Personen des ${entity} auswählen. Nicht Angezeigte werden ignoriert.`,
    },
    {
      id: 'requestConsidered',
      icon: ICONS.MAIL_QUESTION,
      label: `Alle Angedachte des gesamten ${entity} anfragen.`,
      note: `Nicht Angezeigte werden eingeschlossen.`,
    },
  ]

  return items
})

/**
 * Selection helper
 */
const selectionTrigger = ref(0)

function selectConsidered() {
  selectionTrigger.value += 1
}

provide(selectionProjectOrJobHelperKey, {
  selectionTrigger: selectionTrigger,
})

/**
 * Jump target
 */
const jumpTarget = useJumpTarget({
  id: props.id,
  type: props.type,
  el: elRoot,
  registerOnMounted: false,
  hooks: {
    whenInViewport: createAttention,
  },
})

/**
 * Wait for packing to be finished before registering as jump target
 * This is only relevant for the initial mount
 * */

const packingContainerIdForJumpTarget = computed(() => {
  if (props.type === 'stockTypeService' || props.type === 'purchaseOrder') {
    return 'CalendarOperations'
  }

  if (props.type === 'job' || props.type === 'project') {
    return 'CalendarJobs'
  }

  const _exhaustiveCheck: never = props.type
  throw new Error('calendar item type is not supported')
})

let packingFinishedDebounced = () => {}

function onPackingFinished({ id }: { id: PackingContainerId }) {
  if (id !== packingContainerIdForJumpTarget.value) return
  packingFinishedDebounced()
}

async function registerJumpTargetWhenPacked() {
  const promiseTimeout = new Promise((resolve) =>
    setTimeout(resolve, 3 * 1000, 'Resolved by timeout')
  )

  const promiseWaitForPacking = new Promise((resolve) => {
    packingFinishedDebounced = debounce(() => {
      EventHub.$off(EVENT.PACKING_FINISHED, onPackingFinished)
      resolve('Resolved by packing finished')
    }, 100)

    EventHub.$on(EVENT.PACKING_FINISHED, onPackingFinished)
  })

  await Promise.race([promiseTimeout, promiseWaitForPacking])
  await new Promise((resolve) => setTimeout(resolve, 50)) // Wait for a little bit more

  jumpTarget.register()
}

async function createAttention() {
  const el = elRoot.value
  if (!el) throw new Error('el is undefined')

  isHighlightedBecauseJiggeling.value = true
  isJiggeling.value = true

  await jiggle(el)

  isHighlightedBecauseJiggeling.value = false
  isJiggeling.value = false
}

onBeforeUnmount(() => {
  EventHub.$off(EVENT.PACKING_FINISHED, onPackingFinished)
})

registerJumpTargetWhenPacked()
</script>

<style lang="scss">
@use '@/assets/scss/shadows.scss' as shadow;

.PmAppointmentsContainerPure {
  $block: &;
  $name: function.getBlockName($block);

  @include cssVar.define(
    $block,
    'colorBorder',
    cssVar.use($block, 'colorBorder--alternativeColor', color.$gray-400)
  );
  @include cssVar.define(
    $block,
    'colorBackground',
    cssVar.use(
      $block,
      'colorBackground--alternativeColor',
      #{rgba(color.$gray-400, 0.2)}
    )
  );

  // @include mixin.transition-hover((border-color, box-shadow));
  // outline: 2px solid red;

  &.is-jiggeling {
    z-index: 1;
  }

  &-inner {
    box-shadow: inset 0 0 0 3px cssVar.use($block, 'colorBorder');
    border-top: (10px - 3px) solid cssVar.use($block, 'colorBorder');
    background-color: cssVar.use($block, 'colorBackground');
    padding: 3px + 10px;
    display: flex;
    flex-direction: column;

    /* stylelint-disable-next-line plugin/selector-bem-pattern */
    .App:not(.is-dragAndDropInProgress) &:hover,
    .App:not(.is-dragAndDropInProgress) &.is-hover {
      @include cssVar.define($block, 'colorBorder', #{color.$key});

      z-index: 1;
    }

    #{$block}.is-highlighted & {
      @include cssVar.define($block, 'colorBorder', #{color.$key});

      z-index: 1;
    }
  }

  &-header {
    display: flex;
    gap: 4px;
  }

  &-constrainedMarker {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    padding: 4px;
    position: relative;

    &::after {
      content: '';
      inset: 0;
      position: absolute;
      border-radius: constant.$borderRadius-default + 2px;
      box-shadow: inset 0 0 0 2px color.$key;
      opacity: 0;
      transition: opacity constant.$duration-hoverOut;
    }

    &:hover {
      &::after {
        opacity: 1;
        transition-duration: constant.$duration-hoverIn;
      }
    }

    &--start {
      margin-left: -5px;
    }

    &--end {
      margin-right: -5px;
    }
  }

  &-headerDragAndDrop {
    width: 100%;
    display: flex;
    min-width: 0;
  }

  &-headerInner {
    position: sticky;
    left: calc(var(--sidebarLeftWidthWithVisibility) + 8px);
    min-width: 0;
  }

  &-headerContent {
    position: relative;

    &::before {
      @include shadow.drag($offset: -12px);

      border: 1px solid color.$gray-300--alpha;
      border-radius: constant.$borderRadius-default;
      pointer-events: none;
      opacity: 0;
      background-color: color.$white;
      z-index: 0;
    }

    &.is-dragging {
      &::before {
        opacity: 1;
      }
    }
  }

  &-headerContentInner {
    position: relative;
  }

  &-titleContainer {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &-title {
    @include mixin.truncateText;

    font-size: constant.$fontSize-large;
    hyphens: auto;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &-subtitle {
    @include mixin.truncateText;

    hyphens: auto;
  }

  &-meta {
    display: flex;
    align-self: start;
    align-items: flex-start;
    margin-bottom: 5px;
    width: 100%;
    gap: 4px;
  }

  &-actions {
    display: flex;
    gap: 2px;
  }

  &-action {
    @include mixin.transition-hover;

    width: 20px;
    height: 20px;

    &:hover {
      color: color.$key;
    }
  }

  &-icon {
    width: 20px;
    height: 20px;
  }

  &-vehicles {
    font-size: constant.$fontSize-default;
    opacity: 0.5;
    margin-left: -10px;
    margin-right: -10px;
    display: grid;
    grid-auto-columns: var(--dayWidth);
  }

  &-appointments {
    margin-top: 10px;
    margin-left: -13px;
    margin-right: -13px;
    margin-bottom: -13px + 3px;
    position: relative;
  }
}
</style>
